import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

export default function AverageCalc() {
  const [input, setInput] = useState('10, 20, 30, 40, 50');
  const [showWork, setShowWork] = useState(false);
  const c = useMemo(() => {
    const nums = input.split(/[,\s]+/).map(x => parseFloat(x)).filter(n => !isNaN(n));
    const n = nums.length;
    const sum = nums.reduce((a,b)=>a+b, 0);
    const mean = n ? sum / n : 0;
    const sorted = [...nums].sort((a,b)=>a-b);
    const median = n === 0 ? 0 : (n % 2 === 1 ? sorted[(n-1)/2] : (sorted[n/2-1] + sorted[n/2]) / 2);
    const min = n ? Math.min(...nums) : 0;
    const max = n ? Math.max(...nums) : 0;
    const range = max - min;
    // Mode
    const freq = {}; nums.forEach(v => { freq[v] = (freq[v]||0) + 1; });
    const maxF = Math.max(0, ...Object.values(freq));
    const mode = maxF > 1 ? Object.keys(freq).filter(k => freq[k] === maxF).join(', ') : 'No mode';
    // Geometric mean (only if all positive)
    const geometric = nums.every(v=>v>0) && n ? Math.pow(nums.reduce((a,b)=>a*b, 1), 1/n) : null;
    const harmonic = nums.every(v=>v>0) && n ? n / nums.reduce((a,b)=>a + 1/b, 0) : null;
    return { n, sum, mean, median, mode, min, max, range, geometric, harmonic };
  }, [input]);
  return (
    <CalcShell look="cnet" title="Average Calculator" subtitle="Calculate mean, median, mode, and range from a list of numbers.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Numbers (comma or space separated)</label>
            <textarea rows={4} value={input} onChange={e=>setInput(e.target.value)} data-testid="avg-input" className="w-full p-2 border border-slate-300 rounded font-mono text-sm"/>
          </div>
          <p className="text-sm text-slate-600">Count: <b>{c.n}</b></p>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Mean (Arithmetic Average)</div><div className="result-value" data-testid="avg-mean">{c.mean.toFixed(4)}</div></div>
          <table className="data-table">
            <tbody>
              <tr><td>Count</td><td className="text-right">{c.n}</td></tr>
              <tr><td>Sum</td><td className="text-right">{c.sum.toFixed(4)}</td></tr>
              <tr><td>Median</td><td className="text-right font-semibold">{c.median.toFixed(4)}</td></tr>
              <tr><td>Mode</td><td className="text-right">{c.mode}</td></tr>
              <tr><td>Min</td><td className="text-right">{c.min}</td></tr>
              <tr><td>Max</td><td className="text-right">{c.max}</td></tr>
              <tr><td>Range</td><td className="text-right">{c.range}</td></tr>
              {c.geometric !== null && <tr><td>Geometric mean</td><td className="text-right">{c.geometric.toFixed(4)}</td></tr>}
              {c.harmonic !== null && <tr><td>Harmonic mean</td><td className="text-right">{c.harmonic.toFixed(4)}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <StepsPanel steps={[
        `Sum = ${c.sum.toFixed(2)}. Count = ${c.n}.`,
        `Mean = Sum / Count = ${c.sum.toFixed(2)} / ${c.n} = <b>${c.mean.toFixed(4)}</b>`,
        `Sort the numbers to find the median; the middle value is <b>${c.median.toFixed(4)}</b>.`,
      ]}/>
      <div className="mt-4">
        <button onClick={() => setShowWork(v => !v)} data-testid="avg-show-work-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showWork ? '− Hide working' : '+ Show working'}
        </button>
        {showWork && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="avg-work-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
            <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
              <li><b>Sum</b> the values: <span className="font-mono">Σx = {input.split(/[,\s]+/).filter(x=>x).join(' + ')} = {c.sum.toFixed(4)}</span></li>
              <li><b>Count</b> the values: <span className="font-mono">n = {c.n}</span></li>
              <li><b>Arithmetic mean</b>: <span className="font-mono">μ = Σx / n = {c.sum.toFixed(4)} / {c.n} = <b>{c.mean.toFixed(6)}</b></span></li>
              <li><b>Sort</b> and pick the middle → <b>median = {c.median.toFixed(4)}</b></li>
              <li><b>Range</b>: max − min = {c.max} − {c.min} = <b>{c.range}</b></li>
              {c.geometric !== null && <li><b>Geometric mean</b>: <span className="font-mono">(∏x)^(1/n) = <b>{c.geometric.toFixed(4)}</b></span></li>}
              {c.harmonic !== null && <li><b>Harmonic mean</b>: <span className="font-mono">n / Σ(1/x) = <b>{c.harmonic.toFixed(4)}</b></span></li>}
            </ol>
          </div>
        )}
      </div>
    </CalcShell>
  );
}
